import { useId, useState, type FormEvent } from "react";
import { apiFetch } from "../api/client";
import { refusalOf } from "../live/answer";
import { normalizePairCode, pairCodeProblem, typedPairCode } from "../live/pairCode";
import { screenUrl } from "../live/screenLinks";

/**
 * ═══ `/ecran` : OUVRIR UN ÉCRAN DE SALLE AVEC SON CODE ═══
 *
 * La page que le poste du vidéoprojecteur ouvre. Il n'est connecté à rien — ni session,
 * ni messagerie où coller un lien. On y tape le code de l'écran de salle, que la régie
 * montre à tout moment ; le serveur dit de quel événement il s'agit, et la
 * page part sur l'écran de salle, qui garde le code comme clé.
 *
 * ⚠️ HORS DE `/:context` ET SANS LE CHÂSSIS : il n'y a pas encore d'événement, c'est le
 * code qui le désigne. L'adresse est donc la même pour tous — la plus courte à taper.
 *
 * ⚠️ ET DONC SANS AUCUN RÉGLAGE : rien ici ne doit lire `useSettings`/`useT`, qui
 * exigent un événement. `ui/Button` le fait (son « En cours ») — d'où le bouton nu,
 * avec les mêmes classes.
 *
 * Displayed strings are French: this is a French product.
 */
export function ScreenPair() {
  const inputId = useId();
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const code = normalizePairCode(typed);
    const problem = pairCodeProblem(code);
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const answer = await apiFetch<{ token: string; alias: string | null }>(
        "/live/screen-code/claim",
        { api: "apix", method: "POST", body: { code } },
      );
      if (!answer.alias) {
        throw new Error("Cet événement n'a pas d'adresse de site : ouvrez le lien de l'écran.");
      }
      // Une vraie navigation : l'écran de salle monte avec l'événement que le code désigne.
      window.location.replace(screenUrl(window.location.origin, answer.alias, answer.token));
    } catch (cause) {
      setError(refusalOf(cause));
      setBusy(false);
    }
  };

  return (
    <main className="pair">
      <form className="pair__c" onSubmit={submit}>
        <h1>Écran de salle</h1>
        <p className="hint">
          Tapez le code de l'écran de salle, affiché sur la régie.
        </p>
        <label className="a11y" htmlFor={inputId}>
          Code de l'écran
        </label>
        <input
          id={inputId}
          className="pair__code"
          value={typedPairCode(typed)}
          onChange={(e) => {
            setTyped(e.target.value);
            setError(null);
          }}
          placeholder="XXXX-XXXX"
          autoFocus
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          maxLength={9}
        />
        {error ? (
          <p className="live__err" role="alert">
            {error}
          </p>
        ) : null}
        <button type="submit" className="btn" disabled={busy} aria-busy={busy || undefined}>
          {busy ? <span className="spin" aria-hidden="true" /> : null}
          Ouvrir l'écran
        </button>
      </form>
    </main>
  );
}
